import { API_ENDPOINTS, ApiService } from '@services/api/api.service';
import { IBuildingModel } from '@services/api/building/building-model.interface';
import { FlatClientService } from '@services/api/flat/flat-client.service';
import { IFlatModel } from '@services/api/flat/flat-model.interface';
import { IFloorModel } from '@services/api/floor/floor-model.interface';
import { IApiQueryOptions, IDateFilter, IFilterOptions } from '@services/api/models/api-query-params.model';
import { ParkingRequestClientService } from '@services/api/parking-request/parking-request-client.service';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { HardCodedConfigService } from '@services/util/hard-coded-config.service';
import { LogService } from '@services/util/log.service';
import { endOfMonth, format, isSameDay, isWithinInterval, parseISO, startOfMonth, subDays } from 'date-fns';
import { forkJoin, map, Observable, of, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { Building, ParkingData, ParkingLevel } from '../models/parking-overview-model';

@Injectable({
  providedIn: 'root'
})
export class ParkingDataService {
  // Cache for parking data to avoid unnecessary API calls
  private dataCache: Map<string, ParkingData> = new Map<string, ParkingData>();

  // Cache for parking spaces per floor to avoid unnecessary API calls
  private parkingSpacesCache: Map<number, IFlatModel[]> = new Map<number, IFlatModel[]>();

  // Build structure information based on building configuration
  private get buildingStructure(): {
    buildings: Array<{
      id: string;
      name: string;
      levels: Array<{
        id: string;
        name: string;
        totalSpaces: number;
      }>;
    }>;
  } {
    return {
      buildings: this.configService.BUILDING_CONFIG.map((building: { id: number; name: string; parkingFloors: Array<{ id: number }> }) => ({
        id: `building-${building.id}`,
        name: building.name,
        levels: building.parkingFloors.map((floor: { id: number }) => {
          // Get cached parking spaces for this floor
          const parkingSpaces = this.parkingSpacesCache.get(floor.id) || [];
          // Sum up occupancy values from all parking spaces
          const totalCapacity = parkingSpaces.reduce((sum, space) => sum + (space.occupancy || 0), 0);
          this.log.debug(`Building structure for floor ${floor.id}, spaces: ${parkingSpaces.length}, total capacity: ${totalCapacity}`);
          return {
            id: `floor-${floor.id}`,
            name: ``, // Will be populated with actual floor name from API
            totalSpaces: totalCapacity // Sum of occupancy fields from API
          };
        })
      }))
    };
  }

  constructor(
    private readonly parkingRequestClientService: ParkingRequestClientService,
    private readonly flatClientService: FlatClientService,
    private readonly log: LogService,
    private readonly configService: HardCodedConfigService,
    private readonly apiService: ApiService
  ) {}

  /**
   * Fetches parking spaces for a specific floor from the API
   * @param floorId The floor ID to fetch parking spaces for
   * @returns Observable of parking spaces (flats with for_rent=true)
   */
  private fetchParkingSpacesForFloor(floorId: number): Observable<IFlatModel[]> {
    // Check cache first
    if (this.parkingSpacesCache.has(floorId)) {
      return of(this.parkingSpacesCache.get(floorId)!);
    }

    const alternateUrl = this.apiService.getUrlFor(API_ENDPOINTS.floorFlats.replace('{id}', floorId.toString()));
    const options: IApiQueryOptions = {
      filters: {
        for_rent: 1 // true value as 1 for API
      }
    };

    return this.flatClientService.getAllItems(alternateUrl, options).pipe(
      tap((parkingSpaces) => {
        this.log.debug(`Loaded ${parkingSpaces.length} parking spaces for floor ${floorId}`, parkingSpaces);
        this.log.debug(`Loaded ${parkingSpaces.length} parking spaces for floor ${floorId}:`, parkingSpaces);
        // Cache the result
        this.parkingSpacesCache.set(floorId, parkingSpaces);
      })
    );
  }

  /**
   * Loads parking spaces for all floors from the API
   * @returns Observable that completes when all floors have been loaded
   */
  loadAllParkingSpaces(): Observable<boolean> {
    const allFloorIds: number[] = [];

    // Collect all floor IDs from building config
    this.configService.BUILDING_CONFIG.forEach((building) => {
      building.parkingFloors.forEach((floor) => {
        allFloorIds.push(floor.id);
      });
    });

    this.log.debug(`Loading parking spaces for floors: ${allFloorIds.join(', ')}`);
    this.log.debug(`Loading parking spaces for floors: ${allFloorIds.join(', ')}`);

    // Fetch parking spaces for all floors in parallel
    const requests = allFloorIds.map((floorId) => this.fetchParkingSpacesForFloor(floorId));

    return forkJoin(requests).pipe(
      map(() => {
        this.log.debug('All parking spaces loaded successfully');
        this.log.debug('All parking spaces loaded successfully. Cache contents:', this.parkingSpacesCache);
        return true;
      })
    );
  }

  /**
   * Gets parking data for a specific date
   * @param date Date string in YYYY-MM-DD format
   * @returns Observable of ParkingData or undefined if no data found
   */
  getDataForDate(date: string): ParkingData | undefined {
    // Check cache first
    if (this.dataCache.has(date)) {
      return this.dataCache.get(date);
    }

    // Return undefined if no cached data - API integration handles data loading
    return undefined;
  }

  /**
   * Loads parking data for the current month from the API
   * @returns Observable of a boolean indicating if the data was successfully loaded
   */
  loadMonthlyData(): Observable<boolean> {
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    const startDateStr = format(monthStart, 'yyyy-MM-dd');
    const endDateStr = format(monthEnd, 'yyyy-MM-dd');

    this.log.debug(`Loading monthly parking data for ${startDateStr} to ${endDateStr}`);
    return this.loadDataForDateRange(startDateStr, endDateStr);
  }

  /**
   * Loads parking data for a specific month from the API
   * @param monthDate Date representing the month to load data for
   * @returns Observable of a boolean indicating if the data was successfully loaded
   */
  loadMonthlyDataForMonth(monthDate: Date): Observable<boolean> {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);

    const startDateStr = format(monthStart, 'yyyy-MM-dd');
    const endDateStr = format(monthEnd, 'yyyy-MM-dd');

    this.log.debug(`Loading monthly parking data for ${format(monthDate, 'MMM yyyy')}: ${startDateStr} to ${endDateStr}`);
    return this.loadDataForDateRange(startDateStr, endDateStr);
  }

  /**
   * Loads parking data for a range of dates from the API
   * @param startDate Start date string in YYYY-MM-DD format
   * @param endDate End date string in YYYY-MM-DD format
   * @returns Observable of a boolean indicating if the data was successfully loaded
   */
  loadDataForDateRange(startDate: string, endDate: string): Observable<boolean> {
    this.log.debug(`Loading parking data for date range ${startDate} to ${endDate}`);

    // Create date filters using the format expected by the generic API client
    const dateFilterObj: Record<string, IDateFilter> = {
      start_date: {
        date: parseISO(endDate), // End date for start_date[before]
        operator: 'before'
      },
      end_date: {
        date: parseISO(startDate), // Start date for end_date[after]
        operator: 'after'
      }
    };

    this.log.debug('Using date filters:', dateFilterObj);

    // Build API query options
    const options: IApiQueryOptions = {};

    // Add the date filters to options
    if (Object.keys(dateFilterObj).length > 0) {
      options.dateFilters = dateFilterObj;
    }

    return this.parkingRequestClientService.getAllItems('', options).pipe(
      tap((requests) => this.log.debug(`Loaded ${requests.length} parking requests for date range ${startDate} to ${endDate}`)),
      map((requests) => this.processRequestsIntoDataStructure(requests, startDate, endDate)),
      map(() => true)
    );
  }

  /**
   * Processes the parking requests into the ParkingData structure and caches them
   * @param requests Array of parking requests from API
   * @param startDate Start date string in YYYY-MM-DD format
   * @param endDate End date string in YYYY-MM-DD format
   */
  private processRequestsIntoDataStructure(requests: IParkingRequestModel[], startDate: string, endDate: string): void {
    // Create a date range to process
    const dateRange = this.generateDateRange(startDate, endDate);

    // Process each date in the range
    dateRange.forEach((dateStr) => {
      const date = parseISO(dateStr);

      // Initialize parking data structure for this date with default values
      const parkingData: ParkingData = {
        date: dateStr,
        buildings: this.buildingStructure.buildings.map((building) => ({
          id: building.id,
          name: building.name,
          levels: building.levels.map((level) => ({
            id: level.id,
            name: level.name,
            totalSpaces: level.totalSpaces,
            occupiedSpaces: 0, // Will be incremented based on requests
            reservedSpaces: 0 // Will be incremented based on requests
          }))
        }))
      };

      // Filter requests active on this specific date
      const requestsForDate = requests.filter((request) => this.isRequestActiveOnDate(request, dateStr));

      // Process the requests for this date
      requestsForDate.forEach((request) => {
        // Skip requests without parking place info
        if (!request.parkingPlace || !request.parkingPlace['@id']) {
          return;
        }

        // Extract building and level info from parkingPlace
        const parkingPlaceInfo = this.extractParkingPlaceInfo(request.parkingPlace);
        if (!parkingPlaceInfo) {
          return;
        }

        const { buildingId, levelId } = parkingPlaceInfo;

        // Find the building in our structure
        const buildingEntry = parkingData.buildings.find((b) => b.id === buildingId);
        if (!buildingEntry) return;

        // Update building name from API if needed
        if (request.parkingPlace.building?.name && (buildingEntry.name === '' || !buildingEntry.name)) {
          buildingEntry.name = request.parkingPlace.building.name;
        }

        // Find the level in our structure
        const levelEntry = buildingEntry.levels.find((l) => l.id === levelId);
        if (!levelEntry) return;

        // Update level name from API if needed
        if (request.parkingPlace.floor?.name && (levelEntry.name === '' || !levelEntry.name)) {
          levelEntry.name = request.parkingPlace.floor.name;
        }

        // Increment the counts
        levelEntry.occupiedSpaces++;
        levelEntry.reservedSpaces++;
      });

      // Cache the processed data
      this.dataCache.set(dateStr, parkingData);
    });
  }

  /**
   * Extracts building and level information from a parking place
   * @param parkingPlace The parking place object from a request (IFlatModel)
   * @returns Building and level IDs or null if not found
   */
  private extractParkingPlaceInfo(parkingPlace: IFlatModel): { buildingId: string; levelId: string } | null {
    // Check if we have the required building and floor information
    if (!parkingPlace.building || !parkingPlace.floor) {
      return null;
    }

    const building = parkingPlace.building;
    const floor = parkingPlace.floor;

    // Both building and floor have @id properties which are IRIs
    if (!building['@id'] || !floor.id) {
      return null;
    }

    try {
      // Extract building ID from the IRI (format: /api/buildings/{id})
      const buildingIdMatch = building['@id'].match(/\/api\/buildings\/(\d+)/);
      if (!buildingIdMatch) {
        return null;
      }

      const buildingApiId = parseInt(buildingIdMatch[1], 10);
      const mappedBuildingId = `building-${buildingApiId}`;

      // Find the floor ID in the building's parkingFloors
      const buildingConfig = this.configService.BUILDING_CONFIG.find((b) => b.id === buildingApiId);
      if (!buildingConfig) {
        return null;
      }

      // Find the matching floor in the config
      const floorConfig = buildingConfig.parkingFloors.find((f) => f.id === floor.id);
      if (!floorConfig) {
        return null;
      }

      const mappedFloorId = `floor-${floorConfig.id}`;

      return {
        buildingId: mappedBuildingId,
        levelId: mappedFloorId
      };
    } catch (error) {
      this.log.error('Error extracting parking place info', error);
      return null;
    }
  }

  /**
   * Checks if a parking request is active on a specific date
   * @param request The parking request to check
   * @param dateStr Date string in YYYY-MM-DD format
   * @returns True if the request is active on the given date
   */
  private isRequestActiveOnDate(request: IParkingRequestModel, dateStr: string): boolean {
    if (!request.start_date || !request.end_date) {
      return false;
    }

    const date = parseISO(dateStr);
    const startDate = parseISO(request.start_date);
    const endDate = parseISO(request.end_date);

    // Check if this is a single-day request
    const isSingleDayRequest = isSameDay(startDate, endDate);

    if (isSingleDayRequest) {
      // For single-day requests, we count the day as occupied
      return isSameDay(date, startDate);
    } else {
      // For multi-day requests, we count all days EXCEPT the last day
      // (date should be >= startDate AND < endDate)
      return isWithinInterval(date, {
        start: startDate,
        end: subDays(endDate, 1)
      });
    }
  }

  /**
   * Generates an array of date strings between start and end dates (inclusive)
   * @param startDateStr Start date in YYYY-MM-DD format
   * @param endDateStr End date in YYYY-MM-DD format
   * @returns Array of date strings in YYYY-MM-DD format
   */
  private generateDateRange(startDateStr: string, endDateStr: string): string[] {
    const start = parseISO(startDateStr);
    const end = parseISO(endDateStr);
    const dateArray: string[] = [];

    let currentDate = start;
    while (currentDate <= end) {
      dateArray.push(format(currentDate, 'yyyy-MM-dd'));
      currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1));
    }

    return dateArray;
  }

  /**
   * Gets monthly parking data from cache for the current month
   * @returns Array of ParkingData for all cached days in current month
   */
  getMonthlyDataFromCache(): ParkingData[] {
    const now = new Date();
    return this.getMonthlyDataFromCacheForMonth(now);
  }

  /**
   * Gets monthly parking data from cache for a specific month
   * @param monthDate Date representing the month to get data for
   * @returns Array of ParkingData for all cached days in the specified month
   */
  getMonthlyDataFromCacheForMonth(monthDate: Date): ParkingData[] {
    const monthStart = startOfMonth(monthDate);
    const monthEnd = endOfMonth(monthDate);

    const monthlyData: ParkingData[] = [];
    const dateRange = this.generateDateRange(format(monthStart, 'yyyy-MM-dd'), format(monthEnd, 'yyyy-MM-dd'));

    dateRange.forEach((dateStr) => {
      const dayData = this.dataCache.get(dateStr);
      if (dayData) {
        monthlyData.push(dayData);
      }
    });

    this.log.debug(`Retrieved ${monthlyData.length} days of monthly data from cache for ${format(monthDate, 'MMM yyyy')}`);
    return monthlyData;
  }

  /**
   * Calculates average occupancy from an array of parking data
   * @param parkingDataArray Array of ParkingData to calculate average from
   * @returns Average occupied spaces across all provided days
   */
  calculateAverageOccupancy(parkingDataArray: ParkingData[]): number {
    if (!parkingDataArray || parkingDataArray.length === 0) {
      return 0;
    }

    let totalOccupiedAcrossAllDays = 0;
    let validDaysCount = 0;

    parkingDataArray.forEach((dayData) => {
      if (dayData.buildings && dayData.buildings.length > 0) {
        const dayOccupied = dayData.buildings.reduce(
          (sum, building) => sum + building.levels.reduce((levelSum, level) => levelSum + level.occupiedSpaces, 0),
          0
        );
        totalOccupiedAcrossAllDays += dayOccupied;
        validDaysCount++;
      }
    });

    const average = validDaysCount > 0 ? Math.round(totalOccupiedAcrossAllDays / validDaysCount) : 0;
    this.log.debug(`Calculated average occupancy: ${average} from ${validDaysCount} valid days`);
    return average;
  }
}
